import { useCallback, useEffect, useMemo, useRef } from 'react';

import { buildAllEdges, buildJobSkillNodes, DEFAULT_DEEP_FIELD_GRAPH_CONFIG, type DeepFieldGraphNode } from './model';
import { translateGraphLabel } from './translations';
import type { DeepFieldCanvasGraphProps, DeepFieldGraphConfig } from './types';

const BLACK_900 = '#040f16';
const BLACK_800 = '#1a2633';
const GRAY_300 = '#e7e7e7';
const GRAY_400 = '#dadad8';
const GRAY_500 = '#c7c7c5';
const GRAY_600 = '#b0b0ae';
const GRAY_700 = '#9a9a98';
const GRAY_800 = '#6d6d6b';

const DEPTH_GRAYS = [BLACK_900, BLACK_800, GRAY_800, GRAY_700, GRAY_600, GRAY_500];
const DARK_DEPTH_BLUES = ['#bdcbd1', '#9fb2bb', '#8fa4ae', '#718995', '#638394', '#456575'];
const FONT = "'FT Regola Neue', sans-serif";

type GraphAppearancePalette = {
    readonly activeNode: string;
    readonly activeSkillLabel: string;
    readonly alphaScale: number;
    readonly depthColors: readonly string[];
    readonly labelAlphaScale: number;
    readonly roleHaloActive: string;
    readonly roleHaloInactive: string;
};

const LIGHT_GRAPH_PALETTE: GraphAppearancePalette = {
    activeNode: BLACK_900,
    activeSkillLabel: GRAY_800,
    alphaScale: 1,
    depthColors: DEPTH_GRAYS,
    labelAlphaScale: 1,
    roleHaloActive: GRAY_400,
    roleHaloInactive: GRAY_300,
};

const DARK_GRAPH_PALETTE: GraphAppearancePalette = {
    activeNode: '#eef5f7',
    activeSkillLabel: '#bdcbd1',
    alphaScale: 1.8,
    depthColors: DARK_DEPTH_BLUES,
    labelAlphaScale: 2.4,
    roleHaloActive: '#456575',
    roleHaloInactive: '#2b4857',
};

// Decorative background: 30fps halves main-thread cost; the drift is slow enough that it stays smooth.
const FRAME_INTERVAL_MS = 1000 / 30;

type GraphProjection = {
    sx: number;
    sy: number;
    sr: number;
};

const getArrayItem = <T,>(items: readonly T[], index: number, label: string): T => {
    const item = items[index];
    if (item === undefined) {
        throw new Error(`Missing ${label} at index ${index}.`);
    }

    return item;
};

const getFloatValue = (items: Float32Array, index: number): number => items[index] ?? 0;
const getIntValue = (items: Int32Array, index: number): number => items[index] ?? 0;

export function DeepFieldCanvasGraph({
    appearance = 'light',
    className,
    config,
    height = '90vh',
    languageCode = 'en-US',
    paused = false,
    style,
}: DeepFieldCanvasGraphProps = {}) {
    const appearancePalette = appearance === 'dark' ? DARK_GRAPH_PALETTE : LIGHT_GRAPH_PALETTE;
    const { enableDrag, roles, scaleFactor, seed, showLabels } = useMemo<DeepFieldGraphConfig>(
        () => ({
            enableDrag: config?.enableDrag ?? DEFAULT_DEEP_FIELD_GRAPH_CONFIG.enableDrag,
            roles: config?.roles ?? DEFAULT_DEEP_FIELD_GRAPH_CONFIG.roles,
            scaleFactor: config?.scaleFactor ?? DEFAULT_DEEP_FIELD_GRAPH_CONFIG.scaleFactor,
            seed: config?.seed ?? DEFAULT_DEEP_FIELD_GRAPH_CONFIG.seed,
            showLabels: config?.showLabels ?? DEFAULT_DEEP_FIELD_GRAPH_CONFIG.showLabels,
        }),
        [config?.enableDrag, config?.roles, config?.scaleFactor, config?.seed, config?.showLabels],
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const nodesRef = useRef<DeepFieldGraphNode[]>([]);
    const edgesRef = useRef<[number, number][]>([]);
    const rafRef = useRef(0);
    const sizeRef = useRef({ w: 900, h: 700 });
    const scaleRef = useRef(400);
    const dragRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        rotY: 0,
        rotX: 0,
        baseRotY: 0,
        baseRotX: 0,
    });
    const projRef = useRef<GraphProjection[]>([]);
    const sortedRef = useRef<Int32Array>(new Int32Array(0));
    const presenceRef = useRef<Float32Array>(new Float32Array(0));
    const labelsRef = useRef<string[]>([]);
    const lastFrameRef = useRef(0);
    const loopEnabledRef = useRef(true);
    const visibleRef = useRef(true);
    const reducedMotionRef = useRef(false);
    const pausedRef = useRef(paused);
    const pausedAtRef = useRef<number | null>(null);
    const timeOffsetRef = useRef(0);
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    const draw = useCallback(
        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Ported from the website-copy graph to preserve the exact canvas rendering.
        (t: number) => {
            if (loopEnabledRef.current) {
                rafRef.current = requestAnimationFrame(draw);
            }
            if (t - lastFrameRef.current < FRAME_INTERVAL_MS) return;
            lastFrameRef.current = t;
            // Animation time excludes paused stretches so resuming continues from the frozen pose.
            const time = t - timeOffsetRef.current;
            const ctx = ctxRef.current;
            if (!ctx) return;
            const nodes = nodesRef.current;
            const edges = edgesRef.current;
            const N = nodes.length;
            if (N === 0) return;
            const { w: W, h: H } = sizeRef.current;
            const fov = Math.max(W, H) * 1.1;
            const cx = W / 2;
            const cy = H / 2;

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, W, H);

            const ay = -(time * 0.00003 + Math.sin(time * 0.00004) * 0.12);
            const ax = -(Math.sin(time * 0.00003) * 0.14 + Math.sin(time * 0.00008) * 0.05);
            const cosY = Math.cos(ay);
            const sinY = Math.sin(ay);
            const cosX = Math.cos(ax);
            const sinX = Math.sin(ax);
            const sc = scaleRef.current;
            for (let i = 0; i < N; i++) {
                const n = getArrayItem(nodes, i, 'graph node');
                let x = n.bx * cosY + n.bz * sinY;
                let z = -n.bx * sinY + n.bz * cosY;
                let y = n.by * cosX - z * sinX;
                z = n.by * sinX + z * cosX;
                const drift = sc * 0.014;
                const phaseA = i * 0.6;
                const phaseB = i * 0.8 + 2.1;
                const phaseC = i * 0.45 + 4.3;
                x += drift * (Math.sin(time * 0.0008 + phaseA) + 0.4 * Math.sin(time * 0.0005 + phaseB));
                y += drift * (Math.cos(time * 0.0007 + phaseB) + 0.35 * Math.sin(time * 0.0009 + phaseC));
                z += drift * 0.7 * (Math.sin(time * 0.00075 + phaseC) + 0.3 * Math.cos(time * 0.0006 + phaseA));
                n.x = x;
                n.y = y;
                n.z = z;
            }

            const { rotY: drY, rotX: drX } = dragRef.current;
            if (drY !== 0 || drX !== 0) {
                const cY = Math.cos(drY);
                const sY = Math.sin(drY);
                const cX = Math.cos(drX);
                const sX = Math.sin(drX);
                for (let i = 0; i < N; i++) {
                    const n = getArrayItem(nodes, i, 'graph node');
                    const x2 = n.x * cY + n.z * sY;
                    let z2 = -n.x * sY + n.z * cY;
                    const y2 = n.y * cX - z2 * sX;
                    z2 = n.y * sX + z2 * cX;
                    n.x = x2;
                    n.y = y2;
                    n.z = z2;
                }
            }

            let projs = projRef.current;
            if (projs.length !== N) {
                projs = new Array(N);
                for (let i = 0; i < N; i++) projs[i] = { sx: 0, sy: 0, sr: 0 };
                projRef.current = projs;
            }
            for (let i = 0; i < N; i++) {
                const n = getArrayItem(nodes, i, 'graph node');
                const s = fov / (fov + n.z);
                const p = getArrayItem(projs, i, 'graph projection');
                p.sx = cx + n.x * s;
                p.sy = cy + n.y * s;
                p.sr = Math.max(0.5, n.r * s);
            }

            const numGroups = Math.max(roles.length, 1);
            let presence = presenceRef.current;
            if (presence.length !== N) {
                presence = new Float32Array(N);
                presenceRef.current = presence;
            }
            const timePhase = (time / 45000) * Math.PI * 2;
            for (let i = 0; i < N; i++) {
                const phase = (getArrayItem(nodes, i, 'graph node').groupId / numGroups) * Math.PI * 2 - timePhase;
                const raw = 0.5 + 0.5 * Math.sin(phase);
                presence[i] = raw < 0 ? 0 : raw > 1 ? 1 : raw;
            }

            const globalDim = 0;

            function depthGray(z: number): string {
                const scale = fov / (fov + z);
                const idx = Math.min(
                    appearancePalette.depthColors.length - 1,
                    Math.floor((1 - scale) * appearancePalette.depthColors.length * 1.4),
                );
                return (
                    appearancePalette.depthColors[Math.max(0, idx)] ?? appearancePalette.depthColors.at(-1) ?? GRAY_500
                );
            }

            for (let e = 0; e < edges.length; e++) {
                const [i, j] = getArrayItem(edges, e, 'graph edge');
                const presenceA = getFloatValue(presence, i);
                const presenceB = getFloatValue(presence, j);
                const pres = presenceA < presenceB ? presenceA : presenceB;
                if (pres < 0.02) continue;
                const pa = getArrayItem(projs, i, 'graph projection');
                const pb = getArrayItem(projs, j, 'graph projection');
                const dx = pa.sx - pb.sx;
                const dy = pa.sy - pb.sy;
                const sd2 = dx * dx + dy * dy;
                if (sd2 > 90000) continue;
                const sd = Math.sqrt(sd2);
                const a = getArrayItem(nodes, i, 'graph node');
                const b = getArrayItem(nodes, j, 'graph node');
                const sa = fov / (fov + a.z);
                const sb = fov / (fov + b.z);
                const avgScale = (sa + sb) / 2;
                const bothRoles = a.type === 'role' && b.type === 'role';
                const hasRole = a.type === 'role' || b.type === 'role';
                const fade = Math.max(0, 1 - sd / 300);

                const avgZ = (a.z + b.z) / 2;
                ctx.strokeStyle = depthGray(avgZ);
                const dimFactor = 0.45 * (1 - globalDim * 0.55);
                if (bothRoles) {
                    ctx.lineWidth = 0.6 + avgScale * 0.4;
                    ctx.globalAlpha =
                        Math.max(0.02, avgScale * 0.45 * fade * dimFactor) * pres * appearancePalette.alphaScale;
                } else if (hasRole) {
                    ctx.lineWidth = 0.35 + avgScale * 0.25;
                    ctx.globalAlpha =
                        Math.max(0.015, Math.min(a.opacity, b.opacity) * avgScale * 0.35 * fade * dimFactor) *
                        pres *
                        appearancePalette.alphaScale;
                } else {
                    ctx.lineWidth = 0.25 + avgScale * 0.2;
                    ctx.globalAlpha =
                        Math.max(0.015, Math.min(a.opacity, b.opacity) * avgScale * 0.3 * fade * dimFactor) *
                        pres *
                        appearancePalette.alphaScale;
                }
                ctx.beginPath();
                ctx.moveTo(pa.sx, pa.sy);
                ctx.lineTo(pb.sx, pb.sy);
                ctx.stroke();
            }

            let sorted = sortedRef.current;
            if (sorted.length !== N) {
                sorted = new Int32Array(N);
                sortedRef.current = sorted;
            }
            for (let i = 0; i < N; i++) sorted[i] = i;
            sorted.sort((a, b) => getArrayItem(nodes, b, 'graph node').z - getArrayItem(nodes, a, 'graph node').z);

            for (let si = 0; si < N; si++) {
                const i = getIntValue(sorted, si);
                const n = getArrayItem(nodes, i, 'graph node');
                const p = getArrayItem(projs, i, 'graph projection');
                const pres = getFloatValue(presence, i);
                if (pres < 0.02) continue;
                const scale = fov / (fov + n.z);
                const h = 0;

                if (n.type === 'role') {
                    const size = p.sr * 1.5;
                    const r = size * 0.2;
                    ctx.fillStyle = h > 0.1 ? appearancePalette.roleHaloActive : appearancePalette.roleHaloInactive;
                    ctx.globalAlpha = (0.04 * scale + h * 0.2) * pres * appearancePalette.alphaScale;
                    ctx.beginPath();
                    ctx.roundRect(p.sx - size, p.sy - size, size * 2, size * 2, r);
                    ctx.fill();
                }

                const baseAlpha = Math.max(0.03, n.opacity * scale * 0.45 * (1 - globalDim * 0.55));
                const brightAlpha = h > 0.5 ? 1 : Math.max(0.5, n.opacity * scale);
                const alpha = (baseAlpha + (brightAlpha - baseAlpha) * h) * pres;

                const size = p.sr;
                ctx.fillStyle = h > 0.1 ? appearancePalette.activeNode : depthGray(n.z);
                ctx.globalAlpha = alpha * appearancePalette.alphaScale;
                ctx.beginPath();
                if (n.type === 'role') {
                    const r = size * 0.2;
                    ctx.roundRect(p.sx - size, p.sy - size, size * 2, size * 2, r);
                } else {
                    const s = size * 1.3;
                    ctx.moveTo(p.sx, p.sy - s);
                    ctx.lineTo(p.sx + s * 0.866, p.sy + s * 0.5);
                    ctx.lineTo(p.sx - s * 0.866, p.sy + s * 0.5);
                    ctx.closePath();
                }
                ctx.fill();
            }

            if (showLabels) {
                const labels = labelsRef.current;
                ctx.textAlign = 'center';
                for (let si = 0; si < N; si++) {
                    const i = getIntValue(sorted, si);
                    const n = getArrayItem(nodes, i, 'graph node');
                    const p = getArrayItem(projs, i, 'graph projection');
                    const pres = getFloatValue(presence, i);
                    if (pres < 0.02) continue;
                    const scale = fov / (fov + n.z);
                    const h = 0;

                    if (n.type === 'role') {
                        if (scale < 0.45 && h < 0.1) continue;
                        const fs = Math.max(6, Math.round(8 * scale));
                        ctx.font = `600 ${fs}px ${FONT}`;
                        const baseA = 0.25 * scale * (1 - globalDim * 0.6) * pres * appearancePalette.labelAlphaScale;
                        ctx.fillStyle = h > 0.1 ? appearancePalette.activeNode : depthGray(n.z);
                        ctx.globalAlpha = baseA + (1 - baseA) * h;
                        ctx.fillText(labels[i] ?? n.label, p.sx, p.sy - p.sr - 3);
                    } else {
                        if (h < 0.1 && n.r < 2) continue;
                        if (h < 0.1 && scale < 0.6) continue;
                        const fs = Math.max(5, Math.round(6.5 * scale));
                        ctx.font = `500 ${fs}px ${FONT}`;
                        const baseA = 0.15 * scale * (1 - globalDim * 0.6) * pres * appearancePalette.labelAlphaScale;
                        ctx.fillStyle = h > 0.1 ? appearancePalette.activeSkillLabel : depthGray(n.z);
                        ctx.globalAlpha = baseA + (0.9 - baseA) * h;
                        ctx.fillText(labels[i] ?? n.label, p.sx, p.sy - n.r * scale - 2);
                    }
                }
            }
            ctx.globalAlpha = 1;
        },
        [appearancePalette, dpr, roles.length, showLabels],
    );

    useEffect(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;

        ctxRef.current = canvas.getContext('2d');

        const width = container.clientWidth;
        const resolvedHeight = container.clientHeight;
        sizeRef.current = { w: width, h: resolvedHeight };
        canvas.width = width * dpr;
        canvas.height = resolvedHeight * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${resolvedHeight}px`;

        const scale = Math.min(width, resolvedHeight) * scaleFactor;
        scaleRef.current = scale;
        const built = buildJobSkillNodes({
            height: resolvedHeight,
            roles,
            scale,
            seed,
            width,
        });
        nodesRef.current = built;
        edgesRef.current = buildAllEdges(built, scale, roles);
        labelsRef.current = built.map((node) => translateGraphLabel(node.label, languageCode));

        reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        loopEnabledRef.current = visibleRef.current && !reducedMotionRef.current && !pausedRef.current;
        if (pausedRef.current && pausedAtRef.current === null) {
            pausedAtRef.current = performance.now();
        }
        lastFrameRef.current = 0;
        // Always kick off one frame so a paused graph (reduced motion, offscreen, tile open) still renders a static image.
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(rafRef.current);
    }, [draw, dpr, languageCode, roles, scaleFactor, seed]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const observer = new IntersectionObserver(([entry]) => {
            visibleRef.current = entry?.isIntersecting ?? true;
            const shouldLoop = visibleRef.current && !reducedMotionRef.current && !pausedRef.current;
            if (shouldLoop === loopEnabledRef.current) return;
            loopEnabledRef.current = shouldLoop;
            cancelAnimationFrame(rafRef.current);
            if (shouldLoop) {
                rafRef.current = requestAnimationFrame(draw);
            }
        });
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [draw]);

    useEffect(() => {
        if (pausedRef.current === paused) return;
        pausedRef.current = paused;

        if (paused) {
            pausedAtRef.current = performance.now();
            loopEnabledRef.current = false;
            cancelAnimationFrame(rafRef.current);
            return;
        }

        if (pausedAtRef.current !== null) {
            timeOffsetRef.current += performance.now() - pausedAtRef.current;
            pausedAtRef.current = null;
        }
        const shouldLoop = visibleRef.current && !reducedMotionRef.current;
        if (shouldLoop && !loopEnabledRef.current) {
            loopEnabledRef.current = true;
            lastFrameRef.current = 0;
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(draw);
        }
    }, [draw, paused]);

    useEffect(() => {
        if (!enableDrag) return undefined;

        const el = canvasRef.current;
        if (!el) return undefined;

        const d = dragRef.current;
        const onDown = (e: MouseEvent) => {
            d.active = true;
            d.startX = e.clientX;
            d.startY = e.clientY;
            d.baseRotY = d.rotY;
            d.baseRotX = d.rotX;
        };
        const onMove = (e: MouseEvent) => {
            if (!d.active) return;
            d.rotY = d.baseRotY + (e.clientX - d.startX) * 0.005;
            d.rotX = d.baseRotX + (e.clientY - d.startY) * 0.005;
        };
        const onUp = () => {
            d.active = false;
        };
        el.addEventListener('mousedown', onDown);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            el.removeEventListener('mousedown', onDown);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [enableDrag]);

    return (
        <div ref={containerRef} className={className ?? 'w-full relative'} style={{ ...style, height }}>
            <canvas ref={canvasRef} style={{ cursor: 'default', display: 'block' }} />
        </div>
    );
}
